import axios from "axios";
import * as Sentry from "@sentry/browser";
import * as SentryVue from '@sentry/vue';
import { CaptureConsole } from '@sentry/integrations';

export const getThumbnailUrl = (url, width, height, option) => {
	const host = /beta\.(kakao|daum)cdn\.net/.test(url) ? 'img1-beta.daumcdn.net' : 'img1.daumcdn.net';
	return `https://${host}/thumb/${option}${width}x${height}/?fname=${encodeURIComponent(url)}`;
};

export const getUrlParameters = (params) => {
	return Object.entries(params).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
};

export const parseUrlParameters = () => {
	if (!location.search) return {};

	return location.search.slice(1).split('&')
		.map(v => v.split('='))
		.map(arr => ({ [arr[0]]: decodeURIComponent(arr[1]) }))
		.reduce((a, b) => ({ ...a, ...b }), {});
};

export const getBytes = _str => {
	let length = 0;
	const str = _str.toString();

	for (let i=0; i<str.length; i++) {
		length += str.charCodeAt(i) >= 128 ? 2 : 1;
	}

	return length;
};

export const sliceByBytes = (_str, length) => {
	const str = _str.toString();

	let _length = 0, index;
	for (let i=0; i<str.length; i++) {
		const byte = str.charCodeAt(i) >= 128 ? 2 : 1;

		if (_length + byte > length) break;

		_length += byte;
		index = i;
	}

	return str.slice(0, index+1);
};

/**
 * @param file
 * @param fileName
 * @param grpid
 * @param {'image'|'attach'} type
 * @param {object} options
 */
export const uploadFile = async ({ file, fileName='', grpid='' }, type, options = {}) => {
	if (!grpid) {
		throw new Error('miss grpid');
	}
	const formData = new FormData();
	formData.append('type', type);
	if (type === 'image') {
		formData.append('auto_orient', 'true');
	}

	/**
	 * @note file binary data는 반드시 form 마지막에 존재해야 함
	 * https://wiki.daumkakao.com/pages/viewpage.action?pageId=209783200#TWG-%ED%8C%8C%EC%9D%BC%EC%84%9C%EB%B9%84%EC%8A%A4%EA%B0%80%EC%9D%B4%EB%93%9C-POST%EC%97%85%EB%A1%9C%EB%93%9C
	 */
	formData.append('file', file, fileName ? `사본-${fileName}` : undefined);

	// 절대 try~catch를 지우지 마세요
	// 없으면 IE에서 동작안함
	try {
		const {data} = await axios.get(`/_c21_/api/attach/get-upload-url?grpid=${grpid}`, null, {
			headers: {
				'Cache-Control': 'no-cache',
			},
		});
		return await axios.post(data.url, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: e => {
				if (options.onUploadProgress) {
					options.onUploadProgress(e.loaded, e.total);
				}
			},
		});
	} catch (e) {
		throw e;
	}
};

export const argosInspection = async ({fileInfo, fileUsageType, grpid}, success, fail) => {
	const isInspectionMimeType = (mimeType) => {
		const inspectionMimeTypeList = ['image/gif', 'image/webp', 'image/apng'];
		return inspectionMimeTypeList.includes(mimeType);
	};

	const inspectionPolling = async (success, fail, key, times, interval) => {
		if (times <= 0) {
			success();
			return;
		}

		const {data} = await axios.get(`/_c21_/file-legality-status/common/recheck?grpid=${grpid}&checkId=${key}`)
			.catch(() => {
				fail();
			});

		if (data.resultStatus.toUpperCase() === 'BLACK') {
			alert('전기통신사업법에 따라 불법촬영물등에 해당하는 파일이 포함되어 등록할 수 없습니다.');
			fail();
			return;
		}

		if (data.resultStatus.toUpperCase() === 'GRAY') {
			setTimeout(async () => {
				await inspectionPolling(success, fail, key, times - 1, interval);
			}, interval);
			return;
		}

		success();
	};

	if (isInspectionMimeType(fileInfo.property['content-type'])) {
		const {data} = await axios.post(`/_c21_/file-legality-status/common/check?grpid=${grpid}`,
			JSON.stringify({
				fileUsageType: fileUsageType,
				fileUrl: fileInfo.https.image,
				mimeType: fileInfo.property['content-type'],
				fileName: fileInfo.property.filename
			}), {
				headers: {'Content-Type': 'application/json'}
			}).catch(() => { fail(); });

		if (data.resultStatus.toUpperCase() === 'WHITE') {
			success();
			return;
		}

		if (data.resultStatus.toUpperCase() === 'GRAY') {
			await inspectionPolling(success, fail, data.checkId, data.recheckStrategy.count, data.recheckStrategy.interval);
			return;
		}

		alert('전기통신사업법에 따라 불법촬영물등에 해당하는 파일이 포함되어 등록할 수 없습니다.');
		fail();
		return;
	}

	success();
};

export const openPopup = (url, name, { width, height, toolbar='no', location='no', directories='no', status='no', menubar='no', scrollbars='no', resizable='no' } = {}) => {
	const popWindow = window.open(
		url,
		name || '',
		width
			? `width=${width},height=${height},toolbar=${toolbar},location=${location},directories=${directories},status=${status},menubar=${menubar},scrollbars=${scrollbars},resizable=${resizable}`
			: '',
	);
	popWindow.focus();
};

export const escapeHtml = (str) => str.replace(/</g, '&lt;').replace(/>/g, '&gt;');

export class FormBuilder {
	constructor(id) {
		const existedForm = document.getElementById(id);
		if (existedForm !== null) {
			existedForm.parentNode.removeChild(existedForm);
		}

		this.form = document.createElement('form');
		this.form.setAttribute('id', id);
	}

	addInput(name, value) {
		const input = document.createElement('input');
		input.setAttribute('type', 'hidden');
		input.setAttribute('name', name);
		input.setAttribute('value', value);

		this.form.appendChild(input);
	}

	setTarget(target) {
		this.form.setAttribute('target', target);
	}

	setAction(url) {
		this.form.setAttribute('action', url);
	}

	setMethod(method) {
		this.form.setAttribute('method', method.toUpperCase());
	}

	getForm() {
		return this.form;
	}
}

/**
 * IE10 이하 브라우저용 숫자 포매팅 함수
 * IE11부터는 Intl.NumberFormat 쓰세요.
 * @param num
 * @returns {string}
 */
export const formatNumber = num => {
	if(num < 1000) return `${num}`;

	const reg = /(^[+-]?\d+)(\d{3})/;
	let n = num.toString();

	while (reg.test(n)) {
		n = n.replace(reg, '$1' + ',' + '$2');
	}

	return n;
};

/**
 * IE 지원을 위한 CustomEvent polyfill
 * @param name
 * @param detail
 * @returns {AnimationEvent | AudioProcessingEvent | BeforeUnloadEvent | ClipboardEvent | CloseEvent | CompositionEvent | *}
 */
export const createCustomEvent = (name, detail) => {
	const event = document.createEvent('CustomEvent');
	event.initCustomEvent(name, false, false, detail);

	return event;
};

const _initSentry = (Vue, isEditor) => {
	if (SENTRY_DSN_EDITOR || SENTRY_DSN) {
		return;
	}

	const defaultOptions = {
		dsn: isEditor ? SENTRY_DSN_EDITOR : SENTRY_DSN,
		integrations: [
			new CaptureConsole({
				levels: ['error'],
			}),
		],
		ignoreErrors: [
			// 스토리지 용량 관련 에러. 무시
			'QuotaExceededError', // IE 스토리지
			'exceeded the quota', // 크롬 스토리지
			'The quota has been exceeded', // 사파리 스토리지
			// 이미지 업로드 실패 무시.
			'Network Error',
			// 외부 소스 에러 무시.
			'https://t1.daumcdn.net/kas/static/ba.min.js'
		]
	};

	if (Vue) {
		SentryVue.init({
			...defaultOptions,

			Vue,
			tracingOptions: {
				trackComponents: true,
			},
			logErrors: true,
		});
	} else {
		Sentry.init(defaultOptions);
	}
};

export const initSentry = (Vue) => {
	_initSentry(Vue, false);
};

export const initSentryByEditor = (Vue) => {
	_initSentry(Vue, true);
};

export const dataURItoBlob = dataURI => {
	let byteString = atob(dataURI.split(',')[1]);

	let mimeType = dataURI
		.split(',')[0]
		.split(':')[1]
		.split(';')[0]

	let ab = new ArrayBuffer(byteString.length)
	let ia = new Uint8Array(ab)
	for (let i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}

	return new Blob([ab], { type: mimeType });
}
